
import React, { useState, useRef } from 'react';
import { Tree, TreeHealth } from '../types';
import { analyzeTreeImage } from '../services/gemini';
import { Icons } from '../constants';

interface Props {
  inventory: Tree[];
  onUpdateInventory: (inventory: Tree[]) => void;
}

const SitePlanner: React.FC<Props> = ({ inventory, onUpdateInventory }) => {
  const [selectedTreeId, setSelectedTreeId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);

  const handlePlotClick = (e: React.MouseEvent) => {
    if (!plotRef.current) return;
    const rect = plotRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newTree: Tree = {
      id: Math.random().toString(36).substr(2, 9),
      species: 'New Tree',
      diameter: 0,
      health: TreeHealth.GOOD,
      location: 'Property',
      notes: '',
      x,
      y
    };

    onUpdateInventory([...inventory, newTree]);
    setSelectedTreeId(newTree.id);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !selectedTreeId) return;
    setIsUploading(true);
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(',')[1];
      const analysis = await analyzeTreeImage(base64);
      
      const updatedInventory = inventory.map(t => {
        if (t.id === selectedTreeId) {
          return {
            ...t,
            species: analysis?.species || t.species,
            health: (analysis?.health?.toUpperCase() as TreeHealth) || t.health,
            notes: analysis?.recommendation || t.notes,
            photoUrl: reader.result as string
          };
        }
        return t;
      });
      onUpdateInventory(updatedInventory);
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const selectedTree = inventory.find(t => t.id === selectedTreeId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex justify-between items-end">
          <div>
            <h4 className="font-bold text-bark">Interactive Site Plot</h4>
            <p className="text-xs text-stone-500">Tap anywhere on the plot to mark a tree's location.</p>
          </div>
          <span className="text-[10px] font-bold text-gecko uppercase tracking-widest px-2 py-1 bg-leaf/10 rounded">Property View</span>
        </div>
        
        <div 
          ref={plotRef}
          onClick={handlePlotClick}
          className="aspect-square w-full bg-[#E5E7EB] rounded-3xl border-4 border-white shadow-inner relative overflow-hidden cursor-crosshair"
          style={{ backgroundImage: 'radial-gradient(#CBD5E1 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        >
          {/* House placeholder */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1/4 h-1/4 bg-bark/20 rounded-lg flex items-center justify-center border-2 border-bark/30">
            <span className="text-[10px] font-bold uppercase text-bark opacity-40">Main House</span>
          </div>

          {inventory.map(tree => (
            <button
              key={tree.id}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTreeId(tree.id);
              }}
              style={{ left: `${tree.x}%`, top: `${tree.y}%` }}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                selectedTreeId === tree.id ? 'bg-gecko scale-125 z-10 shadow-xl' : 'bg-palm hover:scale-110 z-0'
              }`}
            >
              <Icons.Tree />
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <h4 className="font-bold text-bark">Tree Details</h4>
        {selectedTree ? (
          <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm space-y-6 animate-fade-in">
            {selectedTree.photoUrl ? (
              <img src={selectedTree.photoUrl} alt="Tree" className="w-full h-48 object-cover rounded-xl border border-stone-100" />
            ) : (
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-48 bg-stone-50 border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center justify-center text-stone-400 cursor-pointer hover:bg-stone-100 transition"
              >
                {isUploading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-palm"></div>
                ) : (
                  <>
                    <Icons.Safety />
                    <p className="mt-2 text-xs font-bold uppercase">Upload Photo for AI ID</p>
                  </>
                )}
              </div>
            )}
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />

            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Species Identification</label>
                <input 
                  type="text" 
                  className="w-full mt-1 p-3 bg-stone-50 rounded-lg border-none focus:ring-2 focus:ring-gecko font-bold"
                  value={selectedTree.species}
                  onChange={e => onUpdateInventory(inventory.map(t => t.id === selectedTreeId ? {...t, species: e.target.value} : t))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Health Status</label>
                  <select 
                    className="w-full mt-1 p-3 bg-stone-50 rounded-lg border-none focus:ring-2 focus:ring-gecko text-sm"
                    value={selectedTree.health}
                    onChange={e => onUpdateInventory(inventory.map(t => t.id === selectedTreeId ? {...t, health: e.target.value as TreeHealth} : t))}
                  >
                    {Object.values(TreeHealth).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Diameter (in)</label>
                  <input 
                    type="number"
                    className="w-full mt-1 p-3 bg-stone-50 rounded-lg border-none focus:ring-2 focus:ring-gecko text-sm"
                    value={selectedTree.diameter}
                    onChange={e => onUpdateInventory(inventory.map(t => t.id === selectedTreeId ? {...t, diameter: parseInt(e.target.value) || 0} : t))}
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Property Notes</label>
                <textarea 
                  rows={3}
                  className="w-full mt-1 p-3 bg-stone-50 rounded-lg border-none focus:ring-2 focus:ring-gecko text-sm resize-none"
                  value={selectedTree.notes}
                  onChange={e => onUpdateInventory(inventory.map(t => t.id === selectedTreeId ? {...t, notes: e.target.value} : t))}
                />
              </div>

              <button 
                onClick={() => {
                  onUpdateInventory(inventory.filter(t => t.id !== selectedTreeId));
                  setSelectedTreeId(null);
                }}
                className="w-full py-3 text-red-500 font-bold text-sm hover:bg-red-50 rounded-xl transition"
              >
                Delete Pin
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-stone-100/50 border-2 border-dashed border-stone-200 rounded-3xl p-12 text-center text-stone-400 flex flex-col items-center">
            <Icons.Tree />
            <p className="mt-4 font-bold text-sm uppercase tracking-wider">Select or pin a tree to view details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SitePlanner;
